import { Entity } from '../entity.js';
import { EFFECT_TYPES } from '../combat/statusEffects.js';
import { blinkToward, isInAbilityCircle, isInAbilityCone } from '../combat/abilityArea.js';

/** Broken Wings per-cast radii (FID-ABL-08). */
export const RIVEN_Q_RADII = Object.freeze([260, 270, 280]);
/** Seconds to continue Q cycle before reset. */
export const RIVEN_Q_CYCLE_WINDOW_SEC = 4;
export const RIVEN_W_RADIUS = 300;
export const RIVEN_W_STUN_SEC = 0.75;
/** Blade of the Exile / Wind Slash (FID-ABL-10). */
export const RIVEN_R_DURATION_SEC = 15;
export const RIVEN_R_AD_BONUS = 20;
export const RIVEN_R_RANGE = 900;
export const RIVEN_R_HALF_ANGLE_RAD = Math.PI / 4;

function hasBladeOfExile(caster) {
  return (caster.buffs || []).some((b) => b?.id === 'blade_of_exile');
}

export function createRiven(id = 'riven-1', team = 'blue', x = 300, y = 1300) {
  return new Entity({
    id,
    type: 'champion',
    team,
    x,
    y,
    maxHp: 560,
    maxMp: 0,
    ad: 65,
    ap: 0,
    armor: 33,
    mr: 32,
    ms: 340,
    attackRange: 125,
    attackSpeed: 0.625,
    name: 'Riven',
    radius: 20
  });
}

export function castRivenQ(caster, entities, nowSec = 0) {
  if (!caster || caster.isDead) return false;
  const t = Number(nowSec) || 0;
  const last = Number(caster.rivenQLastAt);
  let idx = Number(caster.rivenQIndex) || 0;
  if (!Number.isFinite(last) || t - last > RIVEN_Q_CYCLE_WINDOW_SEC) {
    idx = 0;
  }
  const radius = RIVEN_Q_RADII[idx] ?? RIVEN_Q_RADII[0];
  for (const e of entities || []) {
    if (!e || e.isDead || e.team === caster.team) continue;
    if (!isInAbilityCircle(e, caster.x, caster.y, radius, caster)) continue;
    e.takeDamage(55 + (caster.ad || 0) * 0.65, caster, 'physical');
  }
  caster.rivenQIndex = (idx + 1) % RIVEN_Q_RADII.length;
  caster.rivenQLastAt = t;
  return true;
}

export function castRivenW(caster, entities = []) {
  if (!caster || caster.isDead) return false;
  const damage = 55 + (caster.ad || 0) * 0.5;
  for (const e of entities || []) {
    if (!e || e.isDead || e.team === caster.team) continue;
    if (!isInAbilityCircle(e, caster.x, caster.y, RIVEN_W_RADIUS, caster)) continue;
    e.takeDamage(damage, caster, 'physical');
    e.statusManager?.addEffect?.(EFFECT_TYPES.STUN, RIVEN_W_STUN_SEC, 0, e.tenacity || 0);
  }
  caster.buffs = caster.buffs || [];
  caster.buffs.push({ id: 'ki_burst', duration: RIVEN_W_STUN_SEC });
  return true;
}

export function castRivenE(caster, tx = null, ty = null) {
  if (!caster || caster.isDead) return false;
  if (Number.isFinite(tx) && Number.isFinite(ty)) {
    blinkToward(caster, tx, ty, 250);
  }
  caster.buffs = caster.buffs || [];
  caster.buffs.push({ id: 'valor', shield: 90 + (caster.ad || 0) * 0.5, duration: 1.5 });
  return true;
}

export function castRivenRWindSlash(caster, entities = [], tx = null, ty = null) {
  if (!caster || caster.isDead || !hasBladeOfExile(caster)) return false;
  const angle = Number.isFinite(tx) && Number.isFinite(ty)
    ? Math.atan2(ty - caster.y, tx - caster.x)
    : (Number(caster.facingAngle) || 0);
  const damage = 100 + (caster.ad || 0) * 1.2;
  for (const e of entities || []) {
    if (!e || e.isDead || e.team === caster.team) continue;
    if (!isInAbilityCone(
      e, caster.x, caster.y, angle, RIVEN_R_RANGE, RIVEN_R_HALF_ANGLE_RAD, caster
    )) continue;
    e.takeDamage(damage, caster, 'physical');
  }
  return true;
}

/** First cast: Blade buff. Recast while buffed: Wind Slash cone. */
export function castRivenR(caster, entities = [], tx = null, ty = null) {
  if (!caster || caster.isDead) return false;
  if (hasBladeOfExile(caster)) {
    return castRivenRWindSlash(caster, entities, tx, ty);
  }
  caster.buffs = caster.buffs || [];
  caster.buffs.push({
    id: 'blade_of_exile',
    adBonus: RIVEN_R_AD_BONUS,
    duration: RIVEN_R_DURATION_SEC
  });
  return true;
}
